import type { FC, MouseEventHandler } from 'react';
import Avatar from '@components/Avatar';
import IconMdi from '@components/IconMdi';
import { Show, ShowFirstMatching } from '@components/Show';
import Tooltip from '@components/Tooltip';
import type { DbComment } from '@domain/schemas/comment.schema';
import { getInitialsFromName } from '@helpers/getInitialsFromName';
import { CheckCircleIcon } from '@heroicons/react/outline';
import { mdiArrowLeftTop } from '@mdi/js';
import clsx from 'clsx';

interface BlockCommentProps {
  comment: DbComment;
  disableActions: boolean;
  idx: number;
  isReviewStatus: boolean;
  onResolveConversation: MouseEventHandler<HTMLButtonElement>;
  onUnresolveConversation: MouseEventHandler<HTMLButtonElement>;
}

export const BlockComment: FC<BlockCommentProps> = ({
  comment,
  disableActions,
  idx,
  isReviewStatus,
  onResolveConversation,
  onUnresolveConversation,
}) => {
  const userName = comment.user.name || 'Unknown User';
  const initials = getInitialsFromName(userName);
  const isResolved = comment.is_resolved;
  const isFirst = idx === 0;

  return (
    <div
      key={comment.id}
      className={clsx('py-2.5', isFirst ? 'px-7' : 'mr-7 mb-2 ml-7 border-l-2 border-gray-200 pl-3')}
      data-testid={`comment-${comment.id}`}
    >
      <div className="flex gap-3">
        <Avatar initials={initials} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className={`text-sm font-medium text-gray-900 ${isResolved ? 'line-through' : ''}`}>{userName}</div>
              <div className={`text-xs text-gray-400 ${isResolved ? 'line-through' : ''}`}>
                {new Date(comment.created_at).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}{' '}
                @{' '}
                {new Date(comment.created_at).toLocaleTimeString('en-GB', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>

            <Show when={isFirst && isReviewStatus}>
              <div className="hover:bg-info-100 rounded-md border border-gray-300 p-1 transition-colors">
                <ShowFirstMatching>
                  <Show when={isResolved}>
                    <Tooltip content="Reopen">
                      <button
                        className={clsx(
                          'flex size-5 items-center justify-center text-gray-600',
                          disableActions && 'cursor-not-allowed opacity-50',
                        )}
                        data-testid="comment-unresolve-button"
                        onClick={onUnresolveConversation}
                        disabled={disableActions}
                      >
                        <IconMdi path={mdiArrowLeftTop} className="size-4" />
                      </button>
                    </Tooltip>
                  </Show>
                  <Show when={!isResolved}>
                    <Tooltip content="Resolve">
                      <button
                        className={clsx(
                          'flex size-5 items-center justify-center text-gray-600',
                          disableActions && 'cursor-not-allowed opacity-50',
                        )}
                        data-testid="comment-resolve-button"
                        onClick={onResolveConversation}
                        disabled={disableActions}
                      >
                        <CheckCircleIcon className="size-4" />
                      </button>
                    </Tooltip>
                  </Show>
                </ShowFirstMatching>
              </div>
            </Show>
          </div>

          <div className="mt-2">
            <p
              className={`text-xs leading-[18px] break-words whitespace-pre-wrap text-gray-500 ${
                isResolved ? 'line-through' : ''
              }`}
            >
              {comment.content}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
